import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { PushRuntime } from '../lib/push';
import {
  type CurrentDevice,
  deletePushSubscription,
  usePushSubscriptions,
} from '../lib/pushSubscriptions';
import { Panel } from './Panel';
import { PushDeviceList } from './PushDeviceList';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';

// Container for the user's push devices (arc 101 slice 09) — owns the read
// (`GET /v1/push/subscriptions`), the revoke, the notice, and the refetch; the
// rendering lives in the `PushDeviceList` presenter. Its own query, so an
// invalidation from the master `PushToggle` refreshes the list. `runtime` is
// injectable for tests (mirrors `PushToggle`).
//
// `unavailable` — this browser cannot say what it is subscribed to, because it has
// no Push API or no service worker went active inside the deadline — renders a
// stated NOTE and the full server list, never an error and never a Retry: no
// button the user can press gives this browser a service worker in this session.

/** The endpoint the presenter badges as "this device" — only a POSITIVE answer
 *  earns the badge, so `unavailable` reads as "unknown", not "not this one". */
function badgedEndpoint(current: CurrentDevice): string | null {
  return current.kind === 'subscribed' ? current.endpoint : null;
}

export function PushDevices(props: {
  token: string | null;
  runtime?: PushRuntime;
  onNotice?: (msg: string) => void;
}): JSX.Element {
  const query = usePushSubscriptions(props.token, props.runtime);

  const remove = async (endpoint: string): Promise<void> => {
    try {
      await deletePushSubscription(endpoint, props.token);
      props.onNotice?.(t('push_devices.removed_notice'));
    } catch {
      props.onNotice?.(t('push_devices.remove_error'));
    } finally {
      await query.refetch();
    }
  };

  return (
    <Panel title={t('push_devices.title')} class="push-devices">
      <p class="card__desc">{t('push_devices.subtitle')}</p>
      <QueryBoundary query={query} loading={<Skeleton height="8rem" />}>
        {(data) => (
          <>
            <Show when={data.currentDevice.kind === 'unavailable'}>
              <p class="card__desc">{t('push_devices.current_unavailable')}</p>
            </Show>
            <PushDeviceList
              subscriptions={data.subscriptions}
              currentEndpoint={badgedEndpoint(data.currentDevice)}
              onRemove={(endpoint) => void remove(endpoint)}
            />
          </>
        )}
      </QueryBoundary>
    </Panel>
  );
}
